import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import SelectAirport from "../components/SelectAirport";
import {
  fetchMETAR,
  fetchTAFV2,
  getSNOWTAM,
  getNOTAMFromAllData,
  getTrainingWXFromAllData,
  getTrainingSnowtamFromAllData,
  parseTrainingWX,
  getLidoNotamText,
  isFlightPlanAirport,
} from "../utilities/helperfunctions";
import {
  fetchAllWeatherData,
  mergeWeatherDataFromBackend,
  formatWeatherDataForDisplay,
} from "../utilities/weatherHelpers";
import { DISABLE_SWIPE } from "../utilities/enums";
import {
  useOverflowObserver,
  formatTafToHTML,
  isTrainingMode,
  getTankeringFuelKg,
} from "../helpers";
import { getContingencyAirportInfo } from "../helpers/contingencyAirportHelpers";
import { useDataServiceContext } from "../hooks/useDataServiceContext";

const AD_WX = "AD_WX";
const RAIM = "RAIM";
const SPACE_WEATHER = "SPACE_WEATHER";

const styles = {
  toggleContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
  },
};

export const Weather = (props) => {
  const { getNOTAMByICAO } = useDataServiceContext();
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [isSwipeDisabled, setIsSwipeDisabled] = useState(false);

  useOverflowObserver(containerRef, contentRef, setIsSwipeDisabled);

  const {
    contLabel,
    contingencyAirportICAO,
    isInAirportsList: isContingencyInAirportsList,
    contingencyAirportObject,
    airportsWithContingency,
    contingencyAirportICAOForStyling,
  } = getContingencyAirportInfo(props.data, props.airports);

  const contingencyAirportICAORef = useRef(contingencyAirportICAO);
  const isFetchingRef = useRef(false);
  const selectedAirport = props.airportSelected?.sICAO
    ? props.airportSelected.sICAO?.[0]
    : props.airportSelected?.sADICAO?.[0];

  const [airportName, setAirportName] = useState(
    props.airportSelected?.sICAO ?? props.airportSelected?.sADICAO
  );
  const [tafRawText, setTafRawText] = useState();
  const [metarRawText, setMetarRawText] = useState();
  const [snowtam, setSnowtam] = useState();
  const [notam, setNotam] = useState();
  const [raim, setRaim] = useState();
  const [spaceWeather, setSpaceWeather] = useState();
  const [icao, setIcao] = useState("");
  const [error, setError] = useState(false);
  const [contingencyWeather, setContingencyWeather] = useState({
    metar: null,
    taf: null,
  });
  const [viewMode, setViewMode] = useState(AD_WX);

  const handleToggle = (event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  };

  const onChangeActiveAirport = (value) => {
    props.onChangeAirport(value);
  };

  const isSelectedAirportApprovedAlternate =
    props.airportSelected?.sADFunction?.[0] === "ApprovedAlternate";
  const isSearchedAirportApprovedAlternate =
    airportName &&
    props.data?.Alt_List?.[0]?.Alternate?.some(
      (alt) =>
        alt?.sADICAO?.[0] === airportName &&
        alt?.sADFunction?.[0] === "ApprovedAlternate"
    );
  const isApprovedAlternate =
    isSelectedAirportApprovedAlternate || isSearchedAirportApprovedAlternate;

  // Fetch weather data for contingency airport if needed
  useEffect(() => {
    const trainingMode = isTrainingMode();

    // Skip contingency weather fetching in training mode
    if (trainingMode) {
      setContingencyWeather({ metar: null, taf: null });
      return;
    }

    const isEraContingency =
      contLabel === "CONT 3% + ERA" &&
      contingencyAirportICAO &&
      !isContingencyInAirportsList &&
      contingencyAirportObject;

    const isAlreadyFetching =
      isFetchingRef.current &&
      contingencyAirportICAORef.current === contingencyAirportICAO;

    if (!isEraContingency || isAlreadyFetching) {
      if (!isEraContingency) {
        setContingencyWeather({ metar: null, taf: null });
        isFetchingRef.current = false;
        contingencyAirportICAORef.current = null;
      }
      return;
    }

    const fetchContingencyWeather = async () => {
      isFetchingRef.current = true;
      contingencyAirportICAORef.current = contingencyAirportICAO;

      try {
        const notamData = await getNOTAMByICAO(contingencyAirportICAO);
        setContingencyWeather({
          metar: notamData?.metar || null,
          taf: notamData?.taf || null,
        });
      } catch (error) {
        console.error("Error fetching contingency airport weather:", error);
        setContingencyWeather({ metar: null, taf: null });
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchContingencyWeather();
  }, [
    contLabel,
    contingencyAirportICAO,
    isContingencyInAirportsList,
    contingencyAirportObject,
  ]);

  const combinedMetar = useMemo(() => {
    if (contingencyWeather.metar) {
      return [...(props.metar || []), contingencyWeather.metar];
    }
    return props.metar || [];
  }, [props.metar, contingencyWeather.metar]);

  const combinedTaf = useMemo(() => {
    if (contingencyWeather.taf) {
      return [...(props.taf || []), contingencyWeather.taf];
    }
    return props.taf || [];
  }, [props.taf, contingencyWeather.taf]);

  const searchWeather = async () => {
    if (icao.length !== 4) {
      setError(true);
      return;
    }

    setError(false);
    const trainingMode = isTrainingMode();
    const icaoUpper = icao.toUpperCase();

    if (trainingMode) {
      // Training mode: use training data
      const trainingWX = getTrainingWXFromAllData(props.data, icaoUpper);

      if (trainingWX?.sWX) {
        const { metar, taf } = parseTrainingWX(trainingWX.sWX);
        setMetarRawText(metar || "METAR data not found");
        setTafRawText(taf || "TAF data not found");
      } else {
        setMetarRawText("METAR data not found");
        setTafRawText("TAF data not found");
      }

      setSnowtam(getTrainingSnowtamFromAllData(props.data, icaoUpper));

      // Get NOTAM from training data
      const notamObj = getNOTAMFromAllData(props.data, icaoUpper);
      const notamText = notamObj?.sNOTAM
        ? Array.isArray(notamObj.sNOTAM)
          ? notamObj.sNOTAM[0]
          : notamObj.sNOTAM
        : "NOTAM data not found";
      setNotam(notamText);

      const raimText = notamObj?.sRAIMS
        ? Array.isArray(notamObj.sRAIMS)
          ? notamObj.sRAIMS[0]
          : notamObj.sRAIMS
        : "RAIM data not found";
      setRaim(raimText);

      const spaceWeatherText = notamObj?.sSpaceWeather
        ? Array.isArray(notamObj.sSpaceWeather)
          ? notamObj.sSpaceWeather[0]
          : notamObj.sSpaceWeather
        : "Space Weather data not found";
      setSpaceWeather(spaceWeatherText);
    } else {
      // Normal mode: use enhanced NOTAM fetching
      const lidoNotamText = getLidoNotamText(props.data, icaoUpper);

      let weatherData;

      if (lidoNotamText) {
        weatherData = await fetchAllWeatherData(icaoUpper);
        weatherData.notam = lidoNotamText;
      } else if (isFlightPlanAirport(icaoUpper, props.data)) {
        weatherData = await fetchAllWeatherData(icaoUpper);
        weatherData.notam = null;
      } else {
        // NOTAM not found locally, try to fetch from backend
        try {
          const notamData = await getNOTAMByICAO(icaoUpper);
          if (notamData) {
            weatherData = await mergeWeatherDataFromBackend(
              notamData,
              icaoUpper,
            );
          } else {
            // No data found, fetch weather data normally
            weatherData = await fetchAllWeatherData(icaoUpper);
          }
        } catch (error) {
          console.error("Error fetching NOTAM data:", error);
          weatherData = await fetchAllWeatherData(icaoUpper);
        }
      }

      const formattedData = formatWeatherDataForDisplay(weatherData);
      setSnowtam(formattedData.snowtam);
      setTafRawText(formattedData.taf);
      setMetarRawText(formattedData.metar);
      setNotam(formattedData.notam);

      const sRAIM = props.data?.sRAIM?.[0] || null;
      const sSpaceWeather = props.data?.sSpaceWeather?.[0] || null;

      setRaim(sRAIM);
      setSpaceWeather(sSpaceWeather);
    }

    setAirportName(icaoUpper);
  };

  const getWeather = () => {
    setError(false);
    const trainingMode = isTrainingMode();
    const icaoCode =
      props.airportSelected?.sICAO ?? props.airportSelected?.sADICAO;
    const icaoString = Array.isArray(icaoCode) ? icaoCode[0] : icaoCode;

    if (trainingMode) {
      // Training mode: use training data
      const trainingWX = getTrainingWXFromAllData(props.data, icaoString);

      if (trainingWX?.sWX) {
        const { metar, taf } = parseTrainingWX(trainingWX.sWX);
        setMetarRawText(metar || "METAR data not found");
        setTafRawText(taf || "TAF data not found");
      } else {
        setMetarRawText("METAR data not found");
        setTafRawText("TAF data not found");
      }

      setSnowtam(getTrainingSnowtamFromAllData(props.data, icaoString));

      const notamObj = getNOTAMFromAllData(props.data, icaoString);
      const notamText = notamObj?.sNOTAM
        ? Array.isArray(notamObj.sNOTAM)
          ? notamObj.sNOTAM[0]
          : notamObj.sNOTAM
        : "NOTAM data not found";
      setNotam(notamText);

      const raimText = notamObj?.sRAIMS
        ? Array.isArray(notamObj.sRAIMS)
          ? notamObj.sRAIMS[0]
          : notamObj.sRAIMS
        : "RAIM data not found";
      setRaim(raimText);

      const spaceWeatherText = notamObj?.sSpaceWeather
        ? Array.isArray(notamObj.sSpaceWeather)
          ? notamObj.sSpaceWeather[0]
          : notamObj.sSpaceWeather
        : "Space Weather data not found";
      setSpaceWeather(spaceWeatherText);
    } else {
      // Normal mode: use props data and enhanced NOTAM fetching
      const snowtam = props.snowtam;
      const airportSnowtam = snowtam.find(
        (s) => s?.sICAO?.[0] === selectedAirport
      );
      setSnowtam(airportSnowtam);

      const airportMetar = combinedMetar.find(
        (m) => m?.station_id?.[0] === selectedAirport
      );
      setMetarRawText(airportMetar?.raw_text ?? "METAR data not found");

      const airportTaf = combinedTaf.find((t) => t.icaoId === selectedAirport);
      setTafRawText(airportTaf?.rawTAF ?? "TAF data not found");

      // Try to get NOTAM from local Lido data only for flight plan airports
      const lidoNotamText = getLidoNotamText(props.data, icaoString);
      setNotam(lidoNotamText || "NOTAM data not found");

      const sRAIM = props.data?.sRAIM?.[0] || null;
      const sSpaceWeather = props.data?.sSpaceWeather?.[0] || null;

      setRaim(sRAIM);
      setSpaceWeather(sSpaceWeather);
    }

    setAirportName(
      props.airportSelected?.sICAO ?? props.airportSelected?.sADICAO
    );
  };

  useEffect(() => {
    getWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.airportSelected,
    props.snowtam,
    combinedMetar,
    combinedTaf,
    props.data,
  ]);

  return (
    <div>
      <SelectAirport
        airports={airportsWithContingency}
        activeAirport={props.airportSelected}
        onChangeActiveAirport={onChangeActiveAirport}
        reqFuel={props.reqFuel}
        tripFuel={props.data?.nTripFuel_KG}
        blockFuel={props.data?.nBlockFuel_KG}
        tankeringValue={getTankeringFuelKg(props.data)}
        contingencyAirportICAO={contingencyAirportICAOForStyling}
      />
      <div className={"content-container"}>
        {navigator.onLine && !isTrainingMode() && (
          <Box
            style={{
              display: "flex",
              marginBottom: "16px",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Box style={{ display: "flex", alignItems: "start" }}>
              <TextField
                value={icao}
                onChange={(event) => setIcao(event.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    searchWeather();
                  }
                }}
                inputProps={{
                  maxLength: 4,
                  style: { textTransform: "uppercase" },
                }}
                placeholder={"ICAO"}
                style={{ width: "140px" }}
                error={error}
                helperText={error && "Must enter four letter ICAO"}
              />
              <IconButton style={{ marginTop: "8px" }} onClick={searchWeather}>
                <SearchIcon style={{ color: "var(--norwegian-blue)" }} />
              </IconButton>
            </Box>
          </Box>
        )}
        {isApprovedAlternate && (
          <Box
            style={{
              color: "red",
              fontWeight: 600,
              marginBottom: "16px",
            }}
          >
            WX/NOTAM SUITABILITY CHECK BY FLIGHT PLANNING SYSTEM NOT PERFORMED
            FOR THIS AERODROME
          </Box>
        )}

        <div style={styles.toggleContainer}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleToggle}>
            <ToggleButton value={AD_WX}>AD WX</ToggleButton>
            <ToggleButton
              value={RAIM}
              style={{
                color: raim && !/NO\s+OUTAGES/i.test(raim) ? "red" : undefined,
              }}
            >
              RAIM
            </ToggleButton>
            <ToggleButton
              value={SPACE_WEATHER}
              style={{
                color:
                  spaceWeather && !/NIL/i.test(spaceWeather)
                    ? "red"
                    : undefined,
              }}
            >
              SpaceWeather
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {viewMode === AD_WX && (
          <>
            <p
              style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}
            >
          METAR {airportName}
        </p>
        <p style={{ marginBottom: "16px", wordWrap: "break-word" }}>
          {metarRawText ?? "METAR data not found"}
        </p>
            <p
              style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}
            >
          TAF {airportName}
        </p>
            <p
              style={{ marginBottom: "16px", wordWrap: "break-word" }}
              id="taf"
            >
          {tafRawText && tafRawText !== "TAF data not found"
            ? formatTafToHTML(tafRawText, airportName)
            : "TAF data not found"}
        </p>
            <p
              style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}
            >
          SNOWTAM {airportName}
        </p>
        <p
          style={{
            marginBottom: "16px",
            wordWrap: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {snowtam?.sSnowtam || "SNOWTAM data not found"}
        </p>
            <p
              style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}
            >
          NOTAM {airportName}
        </p>
        <div
          ref={containerRef}
          className={isSwipeDisabled ? DISABLE_SWIPE : ""}
          style={{ overflowX: "auto" }}
        >
          <div ref={contentRef} style={{ width: "fit-content" }}>
                <pre style={{ marginBottom: "16px", marginTop: 0 }}>
                  {notam}
                </pre>
          </div>
        </div>
          </>
        )}

        {viewMode === RAIM && (
          <>
            <p
              style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}
            >
              RAIM {airportName}
            </p>
            <p
              style={{
                marginBottom: "16px",
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              {raim || "RAIM data not found"}
            </p>
          </>
        )}

        {viewMode === SPACE_WEATHER && (
          <>
            <p
              style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}
            >
              Space Weather {airportName}
            </p>
            <p
              style={{
                marginBottom: "16px",
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              {spaceWeather || "Space Weather data not found"}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
